import { useTranslation } from 'react-i18next';
import { useState, type FC } from 'react';
import {
  SMS_ACTIVITIES_PER_PAGE,
  useGetSmsActivities,
} from 'infrastructure/hooks/ccm';
import BaseTableNew from 'components/atoms/base-table/new';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import { ModuleType } from 'infrastructure/enums';
import classNames from 'classnames';
import { useModule } from 'infrastructure/providers/module-provider';
import { MailOutlined } from '@ant-design/icons';

import s from './styles.module.scss';
import { columns } from './columns';
import ModuleTabs from '../module-tabs';

import type { SortState } from 'infrastructure/types';

interface SmsActivityLoggedProps {
  patientGuid: string;
  showTabs?: boolean;
  module?: ModuleType;
  showTitle?: boolean;
}

const SmsActivityLogged: FC<SmsActivityLoggedProps> = ({
  patientGuid,
  showTabs,
  module = ModuleType.CCM,
  showTitle = true,
}) => {
  const [page, setPage] = useState(1);
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const [createdAtSort, setCreatedAtSort] = useState<SortState>('');

  const { module: internalModule, changeModule: setModuleType } =
    useModule(module);
  const moduleType = showTabs ? internalModule : module;

  const { smsActivity, isLoading } = useGetSmsActivities({
    patientGuid,
    meta: { items: SMS_ACTIVITIES_PER_PAGE, page },
    module: moduleType,
    documentationTimeSort,
    createdAtSort,
  });
  const { t } = useTranslation();

  return (
    <div className={s.wrapper}>
      <div className={classNames({ [s['title-wrapper']]: showTabs })}>
        {showTitle && (
          <h3 className={s.title} data-cy="sms-activity-section">
            {t('labels.smsActivityLogged')}
          </h3>
        )}
        {showTabs && (
          <ModuleTabs
            activeKey={moduleType}
            className={s.tabs}
            onChange={(tab) => {
              setModuleType(tab.key);
              setPage(1);
              setDocumentationTimeSort('');
              setCreatedAtSort('');
            }}
          />
        )}
      </div>
      <BaseTableNew
        items={smsActivity?.items ?? []}
        columns={columns({ documentationTimeSort, createdAtSort })}
        bordered
        changeOddRowColor={false}
        rowHoverable={false}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: SMS_ACTIVITIES_PER_PAGE,
          total: smsActivity?.count ?? 0,
          position: ['bottomRight'],
        }}
        onChangePage={(newPage) => {
          setPage(newPage);
        }}
        dataCy="sms-activity-table"
        emptyText={
          <EmptyTablePlaceholder
            icon={<MailOutlined />}
            text={t('labels.noSMSActivityLogged')}
            column
          />
        }
        onSort={(field, order) => {
          if (!field || !order) {
            setCreatedAtSort('');
            setDocumentationTimeSort('');
          } else if (field === 'createdAt') {
            setCreatedAtSort(order);
            setDocumentationTimeSort('');
          } else if (field === 'documentationTime') {
            setDocumentationTimeSort(order);
            setCreatedAtSort('');
          }
        }}
      />
    </div>
  );
};
export default SmsActivityLogged;
